import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiPlus, FiSearch, FiX } from "react-icons/fi";
import axios from "../../../api/axiosInstance";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import useIsMobile from "../../../hooks/useIsMobile";
import useStockOperationAccess from "../useStockOperationAccess";
import AddStockRequisitionPopup from "../popups/AddStockRequisitionPopup";
import EditStockRequisitionPopup from "../popups/EditStockRequisitionPopup";
import StockMovementPopup from "../popups/StockMovementPopup";
import ViewStockDispatchPopup from "../popups/ViewStockDispatchPopup";
import { stockReturnUrl } from "./stockReturnMessages";
import { stockRequisitionText } from "./stockRequisitionMessages";
import {
  formatStockOpsCurrency,
  formatStockOpsDate,
  formatStockOpsNumber,
  getStockRequisitionStatusMeta,
  normalizeStockStatus,
  truncateStockOpsText,
} from "../stockOperationsUi";

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "confirmed", label: "Confirmed" },
  { value: "returned", label: "Returned" },
  { value: "rejected", label: "Rejected" },
  { value: "approved", label: "Approved" },
  { value: "partially_dispatched", label: "Partially Dispatched" },
  { value: "dispatched", label: "Dispatched" },
  { value: "cancelled", label: "Cancelled" },
];

function resolveInitialStatusFilter(value) {
  const normalized = normalizeStockStatus(value);
  if (STATUS_FILTERS.some((option) => option.value === normalized)) {
    return normalized;
  }
  return "all";
}

function resolveInitialView(value, canViewDispatches) {
  const normalized = String(value || "").trim().toLowerCase();

  if (normalized === "pendingapprovals" || normalized === "approvals") return "pendingApprovals";

  if (normalized === "dispatchqueue" || normalized === "dispatch_queue" || normalized === "queue") {
    return canViewDispatches ? "dispatchQueue" : "requests";
  }

  if (
    normalized === "dispatchhistory" ||
    normalized === "dispatch_history" ||
    normalized === "history"
  ) {
    return canViewDispatches ? "dispatchHistory" : "requests";
  }

  return "requests";
}

function compareDispatchDates(a, b) {
  const aTime = a ? new Date(a).getTime() : 0;
  const bTime = b ? new Date(b).getTime() : 0;
  return bTime - aTime;
}

function normalizeDispatchStatus(value) {
  const status = String(value || "").trim().toLowerCase();

  if (["confirmed", "finalized", "completed", "approved", "valid"].includes(status)) {
    return "confirmed";
  }

  if (
    [
      "draft",
      "processing",
      "queued",
      "awaiting_review",
      "on_hold",
      "in_progress",
      "locked",
      "pending_signature",
      "manual",
      "system",
      "scheduled",
      "dispatching",
      "awaiting_dispatch",
      "auto_generated",
      "retrying",
    ].includes(status)
  ) {
    return "draft";
  }

  if (
    ["cancelled", "voided", "failed", "invalid", "rejected", "expired", "archived", "error"].includes(
      status
    )
  ) {
    return "cancelled";
  }

  return status || "draft";
}

function getDispatchStatusMeta(value) {
  const status = normalizeDispatchStatus(value);

  if (status === "confirmed") {
    return { label: "Confirmed", tone: "dispatched", key: status };
  }

  if (status === "draft") {
    return { label: "Draft", tone: "draft", key: status };
  }

  if (status === "cancelled") {
    return { label: "Cancelled", tone: "cancelled", key: status };
  }

  return {
    label: status ? status.replace(/_/g, " ") : "Unknown",
    tone: "approved",
    key: status || "draft",
  };
}

function formatRequestedQuantity(value) {
  const numeric = Number(value || 0);
  if (Number.isInteger(numeric)) {
    return numeric.toLocaleString("es-ES");
  }
  return formatStockOpsNumber(numeric);
}

function toTimestamp(value) {
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function sumDispatchQuantity(note) {
  return (Array.isArray(note?.lines) ? note.lines : []).reduce(
    (sum, line) => sum + Number(line?.qty_dispatched || 0),
    0
  );
}

function sumDispatchValue(note) {
  return (Array.isArray(note?.lines) ? note.lines : []).reduce((sum, line) => {
    const lineValue = line?.movement?.total_cost ?? line?.total_cost ?? 0;
    return sum + Number(lineValue || 0);
  }, 0);
}

function formatDispatchSummary(note, { draftLabel = false, t = (value) => value } = {}) {
  if (!note) return "-";

  const code = note.dispatch_code || "-";
  const statusKey = normalizeDispatchStatus(note.status);
  if (draftLabel && statusKey === "draft") {
    return `${code} · ${t("Draft")}`;
  }

  const dateLabel = formatStockOpsDate(note.dispatch_date || note.created_at);
  return dateLabel === "-" ? code : `${code} · ${dateLabel}`;
}

export default function StockOperationsWorkspace({
  businessUnitId,
  initialStatusFilter = "all",
  initialView = "requests",
  canViewRequisitions: allowRequisitions = true,
  canViewDispatches: allowDispatches = true,
}) {
  const { languageId } = usePmsTheme();
  const t = useMemo(() => stockRequisitionText(languageId), [languageId]);
  const isMobile = useIsMobile();
  const access = useStockOperationAccess(businessUnitId);
  const canViewRequisitions = allowRequisitions && access.can("requisitions");
  const canViewDispatches = allowDispatches && access.can("dispatched");
  const canDispatch = access.can("dispatched", "dispatch") || access.can("dispatched", "finalize") || access.can("dispatched", "cancel");
  const canAccessOperations = canViewRequisitions || canViewDispatches;
  const [loading, setLoading] = useState(true);
  const [requisitions, setRequisitions] = useState([]);
  const [actionPermissions, setActionPermissions] = useState(null);
  const [dispatchNotes, setDispatchNotes] = useState([]);
  const [requisitionSearch, setRequisitionSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(() =>
    resolveInitialStatusFilter(initialStatusFilter)
  );
  const [activeView, setActiveView] = useState(() =>
    resolveInitialView(initialView, canViewDispatches)
  );
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [dispatchingId, setDispatchingId] = useState(null);
  const [dispatchingNoteId, setDispatchingNoteId] = useState(null);
  const [selectedDispatchId, setSelectedDispatchId] = useState(null);
  const [requestError, setRequestError] = useState(false);
  const [dispatchError, setDispatchError] = useState(false);
  const [retry, setRetry] = useState(0);
  const requestVersion = useRef(0);
  const dispatchVersion = useRef(0);
  const currentScope = useRef("");
  const deepLinkRef = useRef(null);
  const scope = `${access.contextKey}:${canAccessOperations}:${canViewDispatches}`;
  currentScope.current = scope;

  useEffect(() => {
    setEditingId(null); setIsAddOpen(false); setDispatchingId(null); setSelectedDispatchId(null);
  }, [access.contextKey]);

  useEffect(() => {
    setStatusFilter(resolveInitialStatusFilter(initialStatusFilter));
  }, [initialStatusFilter]);

  useEffect(() => {
    setActiveView(resolveInitialView(initialView, canViewDispatches));
  }, [initialView, canViewDispatches]);

  useEffect(() => {
    if (!canViewDispatches && ["dispatchQueue", "dispatchHistory"].includes(activeView)) {
      setActiveView("requests");
    }
  }, [activeView, canViewDispatches]);

  const fetchRequisitions = useCallback(async () => {
    const version = ++requestVersion.current;
    setRequestError(false);
    setActionPermissions(null);
    if (!canAccessOperations || !businessUnitId) {
      setRequisitions([]);
      return;
    }

    try {
      const [res, permissionResponse] = await Promise.all([
        axios.get("/api/purchasing/stock-requisitions", access.requestConfig(canViewRequisitions ? "requisitions" : "dispatched")),
        axios.get("/api/purchasing/stock-requisitions/permissions", access.requestConfig(canViewRequisitions ? "requisitions" : "dispatched")),
      ]);
      if (version !== requestVersion.current || currentScope.current !== scope || !(access.can("requisitions", "view", true) || access.can("dispatched", "view", true))) return;
      if (!Array.isArray(res.data)) throw new Error("Invalid stock requisitions response");
      if (!["request", "approve", "dispatch"].every((action) => typeof permissionResponse.data?.[action] === "boolean")) throw new Error("Invalid stock permissions response");
      setRequisitions(res.data);
      setActionPermissions(permissionResponse.data);
    } catch (error) {
      console.error("[STOCK-OPS][REQUISITIONS][FETCH] Error:", error);
      if (version !== requestVersion.current || currentScope.current !== scope || !(access.can("requisitions", "view", true) || access.can("dispatched", "view", true))) return;
      setRequisitions([]);
      setActionPermissions(null);
      setRequestError(true);
    }
  }, [businessUnitId, canAccessOperations, canViewRequisitions, scope, access]);

  const fetchDispatchNotes = useCallback(async () => {
    const version = ++dispatchVersion.current;
    setDispatchError(false);
    if (!canViewDispatches || !businessUnitId) {
      setDispatchNotes([]);
      return;
    }

    try {
      const res = await axios.get("/api/purchasing/stock-dispatch-notes", access.requestConfig("dispatched"));
      if (version !== dispatchVersion.current || currentScope.current !== scope || !access.can("dispatched", "view", true)) return;
      if (!Array.isArray(res.data)) throw new Error("Invalid stock dispatch response");
      setDispatchNotes(res.data);
    } catch (error) {
      console.error("[STOCK-OPS][DISPATCHES][FETCH] Error:", error);
      if (version !== dispatchVersion.current || currentScope.current !== scope || !access.can("dispatched", "view", true)) return;
      setDispatchNotes([]);
      setDispatchError(true);
    }
  }, [businessUnitId, canViewDispatches, scope, access]);

  useEffect(() => {
    let cancelled = false;

    const loadAll = async () => {
      setRequisitions([]);
      setDispatchNotes([]);
      if (!businessUnitId || !canAccessOperations) {
        setLoading(false);
        return;
      }

      setLoading(true);
      await Promise.all([fetchRequisitions(), fetchDispatchNotes()]);
      if (!cancelled) {
        setLoading(false);
      }
    };

    loadAll();

    return () => {
      cancelled = true;
      requestVersion.current += 1;
      dispatchVersion.current += 1;
    };
  }, [businessUnitId, canAccessOperations, fetchRequisitions, fetchDispatchNotes, retry]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = Number(params.get("document_id"));
    const key = `${businessUnitId}:${id}`;
    if (!loading && !requestError && canViewRequisitions && id > 0 && (!params.get("business_unit_id") || String(businessUnitId) === params.get("business_unit_id")) && deepLinkRef.current !== key) {
      deepLinkRef.current = key;
      setEditingId(id);
    }
  }, [loading, requestError, canViewRequisitions, businessUnitId]);

  const dispatchSummaryByRequisitionKey = useMemo(() => {
    const groups = new Map();

    dispatchNotes.forEach((note) => {
      const primaryKey = note.requisition?.id
        ? `id:${note.requisition.id}`
        : note.requisition?.requisition_code
        ? `code:${note.requisition.requisition_code}`
        : null;

      if (!primaryKey) return;

      if (!groups.has(primaryKey)) {
        groups.set(primaryKey, {
          keys: new Set(),
          notes: [],
        });
      }

      const group = groups.get(primaryKey);
      group.notes.push(note);
      group.keys.add(primaryKey);

      if (note.requisition?.requisition_code) {
        group.keys.add(`code:${note.requisition.requisition_code}`);
      }
    });

    const map = new Map();

    groups.forEach((group) => {
      const notes = group.notes
        .slice()
        .sort((left, right) =>
          compareDispatchDates(
            left.dispatch_date || left.created_at,
            right.dispatch_date || right.created_at
          )
        );

      const confirmedNotes = notes.filter(
        (note) => normalizeDispatchStatus(note.status) === "confirmed"
      );
      const draftNotes = notes.filter((note) => normalizeDispatchStatus(note.status) === "draft");
      const summary = {
        notes,
        confirmedNotes,
        draftNotes,
        latestAnyNote: notes[0] || null,
        latestConfirmedNote: confirmedNotes[0] || null,
        openDraftNote: draftNotes[0] || null,
        confirmedCount: confirmedNotes.length,
        allCount: notes.length,
        totalConfirmedQuantity: confirmedNotes.reduce(
          (sum, note) => sum + sumDispatchQuantity(note),
          0
        ),
        dispatchCodesSearch: notes
          .map((note) => note.dispatch_code)
          .filter(Boolean)
          .join(" "),
      };

      group.keys.forEach((key) => {
        map.set(key, summary);
      });
    });

    return map;
  }, [dispatchNotes]);

  const requisitionRows = useMemo(() => {
    return requisitions.map((requisition) => {
      const lines = Array.isArray(requisition.lines) ? requisition.lines : [];
      const rawStatusKey = String(requisition.status || "").trim().toLowerCase();
      const dispatchSummary =
        dispatchSummaryByRequisitionKey.get(`id:${requisition.id}`) ||
        dispatchSummaryByRequisitionKey.get(`code:${requisition.requisition_code}`);
      const lineCount = Number(
        requisition.lines_count ?? requisition.line_count ?? lines.length
      );
      const requestedQuantity = lines.reduce(
        (sum, line) => sum + Number(line?.quantity || 0),
        0
      );
      const confirmedDispatchedQuantity = lines.some((line) => line.delivered_total != null)
        ? lines.reduce((sum, line) => sum + Number(line.delivered_total || 0), 0)
        : Number(dispatchSummary?.totalConfirmedQuantity || 0);
      const returnedQuantity = lines.reduce((sum, line) => sum + Number(line.returned_total || 0), 0);
      const deliveredQuantity = confirmedDispatchedQuantity;
      const pendingQuantity = Math.max(
        requestedQuantity - confirmedDispatchedQuantity,
        0
      );

      let statusKey = normalizeStockStatus(rawStatusKey);
      if (["approved", "partially_dispatched", "dispatched"].includes(rawStatusKey) && requestedQuantity > 0 && confirmedDispatchedQuantity > 0 && pendingQuantity <= 0) {
        statusKey = "dispatched";
      } else if (
        confirmedDispatchedQuantity > 0 &&
        pendingQuantity > 0 &&
        rawStatusKey === "approved"
      ) {
        statusKey = "partially_dispatched";
      }

      const statusMeta = getStockRequisitionStatusMeta(statusKey);
      const latestConfirmedDispatch = dispatchSummary?.latestConfirmedNote || null;
      const openDraftDispatch = dispatchSummary?.openDraftNote || null;
      const latestDispatchSummary = latestConfirmedDispatch
        ? formatDispatchSummary(latestConfirmedDispatch)
        : openDraftDispatch
        ? formatDispatchSummary(openDraftDispatch, { draftLabel: true, t })
        : "-";
      const latestConfirmedDispatchSummary = latestConfirmedDispatch
        ? formatDispatchSummary(latestConfirmedDispatch)
        : "-";
      const latestDraftSummary = openDraftDispatch
        ? formatDispatchSummary(openDraftDispatch, { draftLabel: true, t })
        : "-";
      const requestTimestamp = toTimestamp(
        requisition.request_date || requisition.created_at
      );
      const expectedTimestamp = toTimestamp(
        requisition.delivery_date || requisition.expected_delivery_date
      );
      const updatedTimestamp = toTimestamp(
        requisition.updated_at || requisition.created_at
      );

      return {
        id: requisition.id,
        code: requisition.requisition_code || "-",
        sourceLocation: requisition.source_location?.name || requisition.source_location?.code || t("Source not selected"),
        requestDate: formatStockOpsDate(requisition.request_date || requisition.created_at),
        requestTimestamp,
        expectedDate: formatStockOpsDate(
          requisition.delivery_date || requisition.expected_delivery_date
        ),
        expectedTimestamp,
        updatedDate: formatStockOpsDate(requisition.updated_at || requisition.created_at),
        updatedTimestamp,
        statusLabel: statusMeta.label,
        statusTone: statusMeta.tone,
        statusKey,
        notes: requisition.notes || "-",
        requestedBy: requisition.requested_by_name || requisition.requested_by_user?.name || requisition.requested_by || requisition.requestedBy || "-",
        lineCount,
        requestedQuantity,
        requestedQuantityLabel: formatRequestedQuantity(requestedQuantity),
        dispatchedQuantity: deliveredQuantity,
        dispatchedQuantityLabel: formatRequestedQuantity(deliveredQuantity),
        returnedQuantityLabel: formatRequestedQuantity(returnedQuantity),
        netConsumptionLabel: formatRequestedQuantity(lines.some((line) => line.net_consumption != null) ? lines.reduce((sum, line) => sum + Number(line.net_consumption || 0), 0) : deliveredQuantity - returnedQuantity),
        pendingQuantity,
        pendingQuantityLabel: formatRequestedQuantity(pendingQuantity),
        dispatchCountConfirmed: dispatchSummary?.confirmedCount || 0,
        dispatchCountAll: dispatchSummary?.allCount || 0,
        latestDispatchId: latestConfirmedDispatch?.id || null,
        latestDispatchCode: latestConfirmedDispatch?.dispatch_code || null,
        latestDispatchSummary,
        latestConfirmedDispatchSummary,
        latestDraftSummary,
        latestDispatchDate: formatStockOpsDate(
          latestConfirmedDispatch?.dispatch_date || latestConfirmedDispatch?.created_at
        ),
        openDraftDispatchId: openDraftDispatch?.id || null,
        openDraftDispatchCode: openDraftDispatch?.dispatch_code || null,
        hasOpenDraftDispatch: Boolean(openDraftDispatch),
        canDispatch:
          canDispatch && (access.can("dispatched", "dispatch") || Boolean(openDraftDispatch)) && canViewDispatches && requisition.permissions?.dispatch === true && rawStatusKey === "approved" && pendingQuantity > 0,
        canApprove: access.can("requisitions", "approve") && rawStatusKey === "confirmed" && (requisition.can_approve ?? requisition.permissions?.approve) === true,
        canEdit: access.can("requisitions", "update") && requisition.can_edit === true,
        canReview: canViewRequisitions || canViewDispatches,
        searchBlob: [
          requisition.source_location?.name,
          requisition.source_location?.code,
          requisition.requisition_code,
          requisition.request_date,
          requisition.created_at,
          requisition.delivery_date,
          requisition.updated_at,
          requisition.notes,
          requisition.requested_by_name || requisition.requested_by,
          requisition.status,
          t(statusMeta.label),
          dispatchSummary?.dispatchCodesSearch,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase(),
      };
    });
  }, [
    canViewDispatches,
    canViewRequisitions,
    canDispatch, access,
    dispatchSummaryByRequisitionKey,
    requisitions,
    t,
  ]);

  const requisitionInfoByKey = useMemo(() => {
    const map = new Map();

    requisitionRows.forEach((row) => {
      map.set(`id:${row.id}`, row);
      map.set(`code:${row.code}`, row);
    });

    return map;
  }, [requisitionRows]);

  const filteredRequisitions = useMemo(() => {
    const query = requisitionSearch.trim().toLowerCase();

    return requisitionRows.filter((row) => {
      if (activeView === "pendingApprovals" ? !row.canApprove : statusFilter !== "all" && row.statusKey !== statusFilter) {
        return false;
      }

      if (query && !row.searchBlob.includes(query)) {
        return false;
      }

      return true;
    });
  }, [requisitionRows, requisitionSearch, statusFilter, activeView]);

  const dispatchQueueRows = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTimestamp = today.getTime();

    return requisitionRows
      .filter((row) => row.canDispatch && row.pendingQuantity > 0)
      .sort((left, right) => {
        const leftOverdue =
          left.expectedTimestamp > 0 && left.expectedTimestamp < todayTimestamp;
        const rightOverdue =
          right.expectedTimestamp > 0 && right.expectedTimestamp < todayTimestamp;

        if (leftOverdue !== rightOverdue) {
          return Number(rightOverdue) - Number(leftOverdue);
        }

        if (left.hasOpenDraftDispatch !== right.hasOpenDraftDispatch) {
          return Number(right.hasOpenDraftDispatch) - Number(left.hasOpenDraftDispatch);
        }

        if (left.expectedTimestamp && right.expectedTimestamp) {
          if (left.expectedTimestamp !== right.expectedTimestamp) {
            return left.expectedTimestamp - right.expectedTimestamp;
          }
        } else if (left.expectedTimestamp || right.expectedTimestamp) {
          return left.expectedTimestamp ? -1 : 1;
        }

        if (left.pendingQuantity !== right.pendingQuantity) {
          return right.pendingQuantity - left.pendingQuantity;
        }

        return left.requestTimestamp - right.requestTimestamp;
      });
  }, [requisitionRows]);

  const filteredDispatchQueueRows = useMemo(() => {
    const query = requisitionSearch.trim().toLowerCase();

    return dispatchQueueRows.filter((row) => {
      if (query && !row.searchBlob.includes(query)) {
        return false;
      }

      return true;
    });
  }, [dispatchQueueRows, requisitionSearch]);

  const dispatchHistoryRows = useMemo(() => {
    return dispatchNotes
      .slice()
      .sort((left, right) =>
        compareDispatchDates(
          left.dispatch_date || left.created_at,
          right.dispatch_date || right.created_at
        )
      )
      .map((note) => {
        const requisitionKey = note.requisition?.id
          ? `id:${note.requisition.id}`
          : note.requisition?.requisition_code
          ? `code:${note.requisition.requisition_code}`
          : null;
        const requisitionInfo = requisitionKey ? requisitionInfoByKey.get(requisitionKey) : null;
        const statusMeta = getDispatchStatusMeta(note.status);
        const dispatchedQuantity = sumDispatchQuantity(note);
        const totalValue = sumDispatchValue(note);
        const requisitionId = requisitionInfo?.id || note.requisition?.id || null;

        return {
          id: note.id,
          code: note.dispatch_code || "-",
          dispatchDate: formatStockOpsDate(note.dispatch_date || note.created_at),
          dispatchTimestamp: toTimestamp(note.dispatch_date || note.created_at),
          requisitionId,
          requisitionCode:
            requisitionInfo?.code || note.requisition?.requisition_code || "-",
          requestedBy:
            requisitionInfo?.requestedBy || note.requisition?.requested_by || "-",
          createdBy: note.created_by || "-",
          statusKey: statusMeta.key,
          statusLabel: statusMeta.label,
          statusTone: statusMeta.tone,
          lineCount: Array.isArray(note.lines) ? note.lines.length : 0,
          dispatchedQuantity,
          dispatchedQuantityLabel: formatRequestedQuantity(dispatchedQuantity),
          returnedQuantityLabel: formatRequestedQuantity((note.lines || []).reduce((sum, line) => sum + Number(line.qty_returned || 0), 0)),
          netConsumptionLabel: formatRequestedQuantity((note.lines || []).reduce((sum, line) => sum + Number(line.net_consumption ?? Number(line.qty_dispatched || 0) - Number(line.qty_returned || 0)), 0)),
          dispatchValueLabel: formatStockOpsCurrency(totalValue),
          isDraft: statusMeta.key === "draft",
          searchBlob: [
            note.dispatch_code,
            note.dispatch_date,
            note.created_at,
            note.created_by,
            note.status,
            t(statusMeta.label),
            requisitionInfo?.code,
            requisitionInfo?.requestedBy,
            requisitionInfo?.notes,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase(),
        };
      });
  }, [dispatchNotes, requisitionInfoByKey, t]);

  const filteredDispatchHistoryRows = useMemo(() => {
    const query = requisitionSearch.trim().toLowerCase();

    return dispatchHistoryRows.filter((row) => {
      if (query && !row.searchBlob.includes(query)) {
        return false;
      }

      return true;
    });
  }, [dispatchHistoryRows, requisitionSearch]);

  const statusOptions = useMemo(() => {
    const counts = requisitionRows.reduce((accumulator, row) => {
      accumulator[row.statusKey] = (accumulator[row.statusKey] || 0) + 1;
      return accumulator;
    }, {});

    return STATUS_FILTERS.map((option) => ({
      ...option,
      count: option.value === "all" ? requisitionRows.length : counts[option.value] || 0,
    }));
  }, [requisitionRows]);

  const requestStats = useMemo(() => {
    const rows = filteredRequisitions;
    return [
      { label: "Visible", value: rows.length },
      { label: "Draft", value: rows.filter((row) => row.statusKey === "draft").length },
      {
        label: "Confirmed",
        value: rows.filter((row) => row.statusKey === "confirmed").length,
      },
      {
        label: "Approved",
        value: rows.filter((row) => row.statusKey === "approved").length,
      },
      {
        label: "Partial",
        value: rows.filter((row) => row.statusKey === "partially_dispatched").length,
      },
      {
        label: "Dispatched",
        value: rows.filter((row) => row.statusKey === "dispatched").length,
      },
    ];
  }, [filteredRequisitions]);

  const queueStats = useMemo(() => {
    const rows = filteredDispatchQueueRows;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTimestamp = today.getTime();

    return [
      { label: "Visible", value: rows.length },
      {
        label: "Ready",
        value: rows.filter((row) => !row.hasOpenDraftDispatch).length,
      },
      {
        label: "Draft Notes",
        value: rows.filter((row) => row.hasOpenDraftDispatch).length,
      },
      {
        label: "Overdue",
        value: rows.filter(
          (row) => row.expectedTimestamp > 0 && row.expectedTimestamp < todayTimestamp
        ).length,
      },
      {
        label: "Pending Qty",
        value: formatRequestedQuantity(
          rows.reduce((sum, row) => sum + row.pendingQuantity, 0)
        ),
      },
      {
        label: "Partial",
        value: rows.filter((row) => row.dispatchedQuantity > 0).length,
      },
    ];
  }, [filteredDispatchQueueRows]);

  const historyStats = useMemo(() => {
    const rows = filteredDispatchHistoryRows;
    const requisitionsCovered = new Set(
      rows.map((row) => row.requisitionCode).filter(Boolean)
    ).size;

    return [
      { label: "Visible", value: rows.length },
      {
        label: "Confirmed",
        value: rows.filter((row) => row.statusKey === "confirmed").length,
      },
      {
        label: "Draft",
        value: rows.filter((row) => row.statusKey === "draft").length,
      },
      {
        label: "Cancelled",
        value: rows.filter((row) => row.statusKey === "cancelled").length,
      },
      {
        label: "Qty Dispatched",
        value: formatRequestedQuantity(
          rows.reduce((sum, row) => sum + row.dispatchedQuantity, 0)
        ),
      },
      { label: "Requisitions", value: requisitionsCovered },
    ];
  }, [filteredDispatchHistoryRows]);

  const viewOptions = useMemo(() => {
    const options = [
      { value: "requests", label: "Requests", count: requisitionRows.length },
    ];

    if (canViewDispatches) {
      options.push(
        {
          value: "dispatchQueue",
          label: "Dispatch Queue",
          count: dispatchQueueRows.length,
        },
        {
          value: "dispatchHistory",
          label: "Dispatch History",
          count: dispatchHistoryRows.length,
        }
      );
    }

    return options;
  }, [
    canViewDispatches,
    dispatchHistoryRows.length,
    dispatchQueueRows.length,
    requisitionRows,
  ]);

  const activeViewMeta = useMemo(() => {
    if (activeView === "dispatchQueue") {
      return {
        subtitle:
          "Approved requests, ordered by urgency. Resume a draft or prepare a dispatch.",
        searchPlaceholder:
          "Search by requisition, requester, notes or draft dispatch...",
        stats: queueStats,
      };
    }

    if (activeView === "dispatchHistory") {
      return {
        subtitle:
          "Trace each dispatch, from draft to confirmed delivery.",
        searchPlaceholder:
          "Search by dispatch code, requisition, requester, creator or status...",
        stats: historyStats,
      };
    }

    return {
      subtitle: activeView === "pendingApprovals"
        ? "Confirmed requisitions you can approve for all their items and source location."
        : "Create requests, review approvals and follow dispatches.",
      searchPlaceholder:
        "Search by code, requester, notes, status or dispatch...",
      stats: requestStats,
    };
  }, [activeView, historyStats, queueStats, requestStats]);

  const handleOpenEdit = (id) => {
    setEditingId(id);
  };

  const handleOpenDispatchWorkflow = (requisitionId) => {
    if (!["dispatch", "finalize", "cancel"].some((action) => access.can("dispatched", action, true))) return;
    setDispatchingNoteId(dispatchNotes.find((note) => Number(note.stock_requisition_id || note.requisition?.id) === Number(requisitionId) && normalizeDispatchStatus(note.status) === "draft")?.id || null);
    setDispatchingId(requisitionId);
  };

  const handleOpenDispatchNote = (dispatchId) => {
    if (!access.can("dispatched", "view", true)) return;
    setSelectedDispatchId(dispatchId);
  };

  const renderRequestActions = (row, { includeOpenFallback = false, compact = false } = {}) => {
    const actions = [];
    const buttonClass = compact ? "so-btn so-btn--compact" : "so-btn";

    if (row.canDispatch) {
      actions.push(
        <button
          key="dispatch"
          type="button"
          className={`${buttonClass} so-btn--success`}
          onClick={() => handleOpenDispatchWorkflow(row.id)}
        >
          {t(row.hasOpenDraftDispatch ? "Resume" : "Dispatch")}
        </button>
      );
    }

    if (row.canReview && ["confirmed", "returned"].includes(row.statusKey)) {
      actions.push(
        <button
          key="review"
          type="button"
          className={`${buttonClass} so-btn--warning`}
          onClick={() => handleOpenEdit(row.id)}
        >
          {t(row.statusKey === "returned" && row.canEdit ? "Correct request" : row.canApprove ? "Review and approve" : "Review")}
        </button>
      );
    }

    if (!actions.length && includeOpenFallback && row.canReview) {
      actions.push(
        <button
          key="open"
          type="button"
          className={`${buttonClass} so-btn--ghost`}
          onClick={() => handleOpenEdit(row.id)}
        >
          {t("Open")}
        </button>
      );
    }

    return actions;
  };

  const renderQueueActions = (row, { compact = false } = {}) => {
    const buttonClass = compact ? "so-btn so-btn--compact" : "so-btn";

    return (
      <button
        type="button"
        className={`${buttonClass} so-btn--success`}
        onClick={() => handleOpenDispatchWorkflow(row.id)}
      >
        {t(row.hasOpenDraftDispatch ? "Resume" : "Dispatch")}
      </button>
    );
  };

  const renderHistoryActions = (row, { compact = false } = {}) => {
    const buttonClass = compact ? "so-btn so-btn--compact" : "so-btn";

    if (row.isDraft && row.requisitionId && canDispatch) {
      return (
        <button
          type="button"
          className={`${buttonClass} so-btn--success`}
          onClick={() => handleOpenDispatchWorkflow(row.requisitionId)}
        >
          {t("Resume")}
        </button>
      );
    }

    return (
      <>
      <button
        type="button"
        className={`${buttonClass} so-btn--ghost`}
        onClick={() => handleOpenDispatchNote(row.id)}
      >
        {t("Open")}
      </button>
      {row.statusKey === "confirmed" && access.can("returns", "create") && <a className={`${buttonClass} so-btn--primary`} href={stockReturnUrl(businessUnitId, row.id)}>{t("New return")}</a>}
      </>
    );
  };

  const emptyRequestsMessage = activeView === "pendingApprovals"
    ? "No requisitions are awaiting your approval."
    : !requisitions.length && statusFilter === "all" && !requisitionSearch.trim()
      ? "No stock requisitions yet."
      : "No stock requisitions match the current filters.";
  const emptyHistoryMessage = !dispatchNotes.length && !requisitionSearch.trim()
    ? "No dispatch notes yet."
    : "No dispatch notes match the current search.";

  const renderRequestsDesktop = () => (
    <div className="so-tableWrap" role="region" aria-label={t("Stock Requisitions")} tabIndex={0}>
      <table className="so-table so-table--requisitions">
        <colgroup>
          <col style={{ width: "168px" }} />
          <col style={{ width: "94px" }} />
          <col style={{ width: "94px" }} />
          <col style={{ width: "144px" }} />
          <col style={{ width: "120px" }} />
          <col style={{ width: "82px" }} />
          <col style={{ width: "82px" }} />
          <col style={{ width: "82px" }} />
          <col style={{ width: "74px" }} />
          <col style={{ width: "148px" }} />
          <col style={{ width: "auto" }} />
          <col style={{ width: "98px" }} />
        </colgroup>
        <thead>
          <tr>
            <th>{t("Code")}</th>
            <th>{t("Request Date")}</th>
            <th>{t("Expected")}</th>
            <th>{t("Status")}</th>
            <th>{t("Requested By")}</th>
            <th className="so-number">{t("Requested")}</th>
            <th className="so-number">{t("Dispatched")}</th>
            <th className="so-number">{t("Pending")}</th>
            <th className="so-number">{t("Dispatches")}</th>
            <th>{t("Last Dispatch")}</th>
            <th className="so-colNotes">{t("Notes")}</th>
            <th className="so-colAction">{t("Action")}</th>
          </tr>
        </thead>
        <tbody>
          {filteredRequisitions.length ? (
            filteredRequisitions.map((row) => (
              <tr key={row.id}>
                <td>
                  <button
                    type="button"
                    className="so-codeButton"
                    onClick={() => handleOpenEdit(row.id)}
                  >
                    {row.code}
                  </button>
                  <div className="so-muted" title={`${t("Source location")}: ${row.sourceLocation}`}>{row.sourceLocation}</div>
                </td>
                <td className="so-muted">{row.requestDate}</td>
                <td className="so-muted">{row.expectedDate}</td>
                <td>
                  <span className={`so-status so-status--${row.statusTone}`}>
                    {t(row.statusLabel)}
                  </span>
                </td>
                <td className="so-muted" title={row.requestedBy}>
                  <span className="so-ellipsis">
                    {row.requestedBy}
                  </span>
                </td>
                <td className="so-number so-strong">{row.requestedQuantityLabel}</td>
                <td className="so-number">{row.dispatchedQuantityLabel}<div className="so-muted" style={{ fontSize: 11 }}>{t("Returned quantity")}: {row.returnedQuantityLabel} · {t("Net consumption")}: {row.netConsumptionLabel}</div></td>
                <td className="so-number">{row.pendingQuantityLabel}</td>
                <td className="so-number">{row.dispatchCountConfirmed}</td>
                <td className="so-muted" title={row.latestDispatchSummary}>
                  {row.latestDispatchId ? (
                    <button
                      type="button"
                      className="so-linkButton so-ellipsis"
                      onClick={() => handleOpenDispatchNote(row.latestDispatchId)}
                    >
                      {row.latestDispatchSummary}
                    </button>
                  ) : (
                    <span className="so-ellipsis">
                      {row.latestDispatchSummary}
                    </span>
                  )}
                </td>
                <td className="so-cellNotes" title={row.notes}>
                  <span className="so-ellipsis so-muted so-cellNotesText">
                    {truncateStockOpsText(row.notes, 150)}
                  </span>
                </td>
                <td className="so-cellAction">
                  <div className="so-actions so-actions--cell">
                    {renderRequestActions(row, { compact: true })}
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="12" className="so-empty">
                <span role="status">{t(emptyRequestsMessage)}</span>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderQueueDesktop = () => (
    <div className="so-tableWrap" role="region" aria-label={t("Dispatch Queue")} tabIndex={0}>
      <table className="so-table so-table--queue">
        <colgroup>
          <col style={{ width: "168px" }} />
          <col style={{ width: "94px" }} />
          <col style={{ width: "144px" }} />
          <col style={{ width: "120px" }} />
          <col style={{ width: "82px" }} />
          <col style={{ width: "82px" }} />
          <col style={{ width: "82px" }} />
          <col style={{ width: "148px" }} />
          <col style={{ width: "148px" }} />
          <col style={{ width: "auto" }} />
          <col style={{ width: "98px" }} />
        </colgroup>
        <thead>
          <tr>
            <th>{t("Requisition")}</th>
            <th>{t("Expected")}</th>
            <th>{t("Status")}</th>
            <th>{t("Requested By")}</th>
            <th className="so-number">{t("Requested")}</th>
            <th className="so-number">{t("Dispatched")}</th>
            <th className="so-number">{t("Pending")}</th>
            <th>{t("Draft Note")}</th>
            <th>{t("Last Confirmed")}</th>
            <th className="so-colNotes">{t("Notes")}</th>
            <th className="so-colAction">{t("Action")}</th>
          </tr>
        </thead>
        <tbody>
          {filteredDispatchQueueRows.length ? (
            filteredDispatchQueueRows.map((row) => (
              <tr key={row.id}>
                <td>
                  <button
                    type="button"
                    className="so-codeButton"
                    onClick={() => handleOpenEdit(row.id)}
                  >
                    {row.code}
                  </button>
                  <div className="so-muted" title={`${t("Source location")}: ${row.sourceLocation}`}>{row.sourceLocation}</div>
                </td>
                <td className="so-muted">{row.expectedDate}</td>
                <td>
                  <span className={`so-status so-status--${row.statusTone}`}>
                    {t(row.statusLabel)}
                  </span>
                </td>
                <td className="so-muted" title={row.requestedBy}>
                  <span className="so-ellipsis">
                    {row.requestedBy}
                  </span>
                </td>
                <td className="so-number so-strong">{row.requestedQuantityLabel}</td>
                <td className="so-number">{row.dispatchedQuantityLabel}<div className="so-muted" style={{ fontSize: 11 }}>{t("Returned quantity")}: {row.returnedQuantityLabel} · {t("Net consumption")}: {row.netConsumptionLabel}</div></td>
                <td className="so-number">{row.pendingQuantityLabel}</td>
                <td className="so-muted" title={row.latestDraftSummary}>
                  <span className="so-ellipsis">
                    {row.latestDraftSummary}
                  </span>
                </td>
                <td className="so-muted" title={row.latestConfirmedDispatchSummary}>
                  <span className="so-ellipsis">
                    {row.latestConfirmedDispatchSummary}
                  </span>
                </td>
                <td className="so-cellNotes" title={row.notes}>
                  <span className="so-ellipsis so-muted so-cellNotesText">
                    {truncateStockOpsText(row.notes, 140)}
                  </span>
                </td>
                <td className="so-cellAction">
                  <div className="so-actions so-actions--cell">
                    {renderQueueActions(row, { compact: true })}
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="11" className="so-empty">
                {t("No requisitions are waiting in the dispatch queue.")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderHistoryDesktop = () => (
    <div className="so-tableWrap" role="region" aria-label={t("Dispatch History")} tabIndex={0}>
      <table className="so-table so-table--history">
        <colgroup>
          <col style={{ width: "168px" }} />
          <col style={{ width: "94px" }} />
          <col style={{ width: "168px" }} />
          <col style={{ width: "120px" }} />
          <col style={{ width: "120px" }} />
          <col style={{ width: "130px" }} />
          <col style={{ width: "82px" }} />
          <col style={{ width: "72px" }} />
          <col style={{ width: "110px" }} />
          <col style={{ width: "98px" }} />
        </colgroup>
        <thead>
          <tr>
            <th>{t("Dispatch Note")}</th>
            <th>{t("Date")}</th>
            <th>{t("Requisition")}</th>
            <th>{t("Requested By")}</th>
            <th>{t("Created By")}</th>
            <th>{t("Status")}</th>
            <th className="so-number">{t("Qty")}</th>
            <th className="so-number">{t("Lines")}</th>
            <th className="so-number">{t("Value")}</th>
            <th className="so-colAction">{t("Action")}</th>
          </tr>
        </thead>
        <tbody>
          {filteredDispatchHistoryRows.length ? (
            filteredDispatchHistoryRows.map((row) => (
              <tr key={row.id}>
                <td>
                  <button
                    type="button"
                    className="so-codeButton"
                    onClick={() =>
                      row.isDraft && row.requisitionId && canDispatch
                        ? handleOpenDispatchWorkflow(row.requisitionId)
                        : handleOpenDispatchNote(row.id)
                    }
                  >
                    {row.code}
                  </button>
                </td>
                <td className="so-muted">{row.dispatchDate}</td>
                <td className="so-muted">
                  {row.requisitionId ? (
                    <button
                      type="button"
                      className="so-codeButton"
                      onClick={() => handleOpenEdit(row.requisitionId)}
                    >
                      {row.requisitionCode}
                    </button>
                  ) : (
                    row.requisitionCode
                  )}
                </td>
                <td className="so-muted" title={row.requestedBy}>
                  <span className="so-ellipsis">
                    {row.requestedBy}
                  </span>
                </td>
                <td className="so-muted" title={row.createdBy}>
                  <span className="so-ellipsis">
                    {row.createdBy}
                  </span>
                </td>
                <td>
                  <span className={`so-status so-status--${row.statusTone}`}>
                    {t(row.statusLabel)}
                  </span>
                </td>
                <td className="so-number so-strong">{row.dispatchedQuantityLabel}<div className="so-muted" style={{ fontSize: 11 }}>{t("Returned quantity")}: {row.returnedQuantityLabel} · {t("Net consumption")}: {row.netConsumptionLabel}</div></td>
                <td className="so-number">{row.lineCount}</td>
                <td className="so-number">{row.dispatchValueLabel}</td>
                <td className="so-cellAction">
                  <div className="so-actions so-actions--cell">
                    {renderHistoryActions(row, { compact: true })}
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="10" className="so-empty">
                <span role="status">{t(emptyHistoryMessage)}</span>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderRequestsMobile = () => (
    <div className="so-mobileList">
      {filteredRequisitions.length ? (
        filteredRequisitions.map((row) => (
          <article key={row.id} className="so-mobileCard">
            <div className="so-mobileTop">
              <div>
                <button
                  type="button"
                  className="so-codeButton so-mobileCodeButton"
                  onClick={() => handleOpenEdit(row.id)}
                >
                  {row.code}
                </button>
                <div className="so-mobileTitle" title={row.notes}>
                  {truncateStockOpsText(row.notes === "-" ? t("No notes") : row.notes, 96)}
                </div>
              </div>
              <span className={`so-status so-status--${row.statusTone}`}>{t(row.statusLabel)}</span>
            </div>

            <div className="so-mobileRows">
              <div>
                <div className="so-mobileFieldLabel">{t("Source location")}</div>
                <div className="so-mobileFieldValue">{row.sourceLocation}</div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Request Date")}</div>
                <div className="so-mobileFieldValue">{row.requestDate}</div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Expected")}</div>
                <div className="so-mobileFieldValue">{row.expectedDate}</div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Requested By")}</div>
                <div className="so-mobileFieldValue">{row.requestedBy}</div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Requested")}</div>
                <div className="so-mobileFieldValue">{row.requestedQuantityLabel}</div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Dispatched")}</div>
                <div className="so-mobileFieldValue">{row.dispatchedQuantityLabel}<div className="so-muted">{t("Returned quantity")}: {row.returnedQuantityLabel} · {t("Net consumption")}: {row.netConsumptionLabel}</div></div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Pending")}</div>
                <div className="so-mobileFieldValue">{row.pendingQuantityLabel}</div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Dispatch Notes")}</div>
                <div className="so-mobileFieldValue">{row.dispatchCountConfirmed}</div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Last Dispatch")}</div>
                <div className="so-mobileFieldValue">{row.latestDispatchSummary}</div>
              </div>
            </div>

            <div className="so-mobileActions">
              {renderRequestActions(row, { includeOpenFallback: true })}
            </div>
          </article>
        ))
      ) : (
        <div className="so-empty" role="status">{t(emptyRequestsMessage)}</div>
      )}
    </div>
  );

  const renderQueueMobile = () => (
    <div className="so-mobileList">
      {filteredDispatchQueueRows.length ? (
        filteredDispatchQueueRows.map((row) => (
          <article key={row.id} className="so-mobileCard">
            <div className="so-mobileTop">
              <div>
                <button
                  type="button"
                  className="so-codeButton so-mobileCodeButton"
                  onClick={() => handleOpenEdit(row.id)}
                >
                  {row.code}
                </button>
                <div className="so-mobileTitle" title={row.notes}>
                  {truncateStockOpsText(row.notes === "-" ? t("No notes") : row.notes, 96)}
                </div>
              </div>
              <span className={`so-status so-status--${row.statusTone}`}>{t(row.statusLabel)}</span>
            </div>

            <div className="so-mobileRows">
              <div>
                <div className="so-mobileFieldLabel">{t("Source location")}</div>
                <div className="so-mobileFieldValue">{row.sourceLocation}</div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Expected")}</div>
                <div className="so-mobileFieldValue">{row.expectedDate}</div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Requested By")}</div>
                <div className="so-mobileFieldValue">{row.requestedBy}</div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Requested")}</div>
                <div className="so-mobileFieldValue">{row.requestedQuantityLabel}</div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Dispatched")}</div>
                <div className="so-mobileFieldValue">{row.dispatchedQuantityLabel}<div className="so-muted">{t("Returned quantity")}: {row.returnedQuantityLabel} · {t("Net consumption")}: {row.netConsumptionLabel}</div></div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Pending")}</div>
                <div className="so-mobileFieldValue">{row.pendingQuantityLabel}</div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Draft Note")}</div>
                <div className="so-mobileFieldValue">{row.latestDraftSummary}</div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Last Confirmed")}</div>
                <div className="so-mobileFieldValue">{row.latestConfirmedDispatchSummary}</div>
              </div>
            </div>

            <div className="so-mobileActions">{renderQueueActions(row)}</div>
          </article>
        ))
      ) : (
        <div className="so-empty">{t("No requisitions are waiting in the dispatch queue.")}</div>
      )}
    </div>
  );

  const renderHistoryMobile = () => (
    <div className="so-mobileList">
      {filteredDispatchHistoryRows.length ? (
        filteredDispatchHistoryRows.map((row) => (
          <article key={row.id} className="so-mobileCard">
            <div className="so-mobileTop">
              <div>
                <button
                  type="button"
                  className="so-codeButton so-mobileCodeButton"
                  onClick={() =>
                    row.isDraft && row.requisitionId && canDispatch
                      ? handleOpenDispatchWorkflow(row.requisitionId)
                      : handleOpenDispatchNote(row.id)
                  }
                >
                  {row.code}
                </button>
                <div className="so-mobileTitle">{row.requisitionCode}</div>
              </div>
              <span className={`so-status so-status--${row.statusTone}`}>{t(row.statusLabel)}</span>
            </div>

            <div className="so-mobileRows">
              <div>
                <div className="so-mobileFieldLabel">{t("Dispatch Date")}</div>
                <div className="so-mobileFieldValue">{row.dispatchDate}</div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Requested By")}</div>
                <div className="so-mobileFieldValue">{row.requestedBy}</div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Created By")}</div>
                <div className="so-mobileFieldValue">{row.createdBy}</div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Qty")}</div>
                <div className="so-mobileFieldValue">{row.dispatchedQuantityLabel}<div className="so-muted">{t("Returned quantity")}: {row.returnedQuantityLabel} · {t("Net consumption")}: {row.netConsumptionLabel}</div></div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Lines")}</div>
                <div className="so-mobileFieldValue">{row.lineCount}</div>
              </div>
              <div>
                <div className="so-mobileFieldLabel">{t("Value")}</div>
                <div className="so-mobileFieldValue">{row.dispatchValueLabel}</div>
              </div>
            </div>

            <div className="so-mobileActions">{renderHistoryActions(row)}</div>
          </article>
        ))
      ) : (
        <div className="so-empty" role="status">{t(emptyHistoryMessage)}</div>
      )}
    </div>
  );

  const renderStats = (stats) => (
    <div className="so-inlineStats" aria-live="polite">
      {stats.filter((stat) => ["Visible", "Draft Notes", "Overdue", "Pending Qty", "Qty Dispatched", "Requisitions"].includes(stat.label)).map((stat) => (
        <span className="so-inlineStat" key={stat.label}>
          {t(stat.label)} <strong className="so-inlineStatValue">{stat.value}</strong>
        </span>
      ))}
    </div>
  );

  const renderActiveView = () => {
    if (activeView === "dispatchQueue") {
      return isMobile ? renderQueueMobile() : renderQueueDesktop();
    }

    if (activeView === "dispatchHistory") {
      return isMobile ? renderHistoryMobile() : renderHistoryDesktop();
    }

    return isMobile ? renderRequestsMobile() : renderRequestsDesktop();
  };

  if (!businessUnitId) return <p>{t("Select a business unit.")}</p>;

  if (!canAccessOperations) {
    return (
      <>
        <section className="so-card">
          <div className="so-empty" role="alert">{t("You do not have access to stock requisitions.")}</div>
        </section>
      </>
    );
  }

  if (!businessUnitId) return (
    <>
      <section className="so-card"><div className="so-empty" role="status">{t("Select a business unit to view stock requisitions.")}</div></section>
    </>
  );

  return (
    <>

      <section className="so-card" aria-label={t("Stock Requisitions")}>
        <div className="so-cardHead">
          <div className="so-cardHeadMain">
            <h2 className="so-cardTitle">{t("Stock Requisitions")}</h2>
            <p className="so-cardSubtitle">{t(activeViewMeta.subtitle)}</p>
          </div>
          {canViewRequisitions && access.can("requisitions", "create") && access.can("requisitions", "update") && (actionPermissions?.can_create ?? actionPermissions?.request) && !requestError ? (
            <button type="button" className="so-btn so-btn--primary" onClick={() => setIsAddOpen(true)}>
              <FiPlus aria-hidden="true" /> {t("New requisition")}
            </button>
          ) : null}
        </div>

        {loading ? (
          <div className="so-empty" role="status">{t("Loading stock operations...")}</div>
        ) : requestError || dispatchError ? (
          <div className="so-fetchError" role="alert">
            <span>{t("Stock operations could not be loaded. Retry to see accurate quantities.")}</span>
            <button type="button" className="so-btn" onClick={() => setRetry((value) => value + 1)}>{t("Retry")}</button>
          </div>
        ) : (
          <>
            <div className="so-toolbar">
              <div className="so-viewTabs" role="group" aria-label={t("Stock requisition views")}>
                {viewOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={activeView === option.value}
                    className={`so-viewTab ${activeView === option.value ? "so-viewTab--active" : ""}`}
                    onClick={() => setActiveView(option.value)}
                  >
                    {t(option.label)} <span>{option.count}</span>
                  </button>
                ))}
              </div>
              <div className="so-toolbarMain">
                <div className="so-searchField">
                  <FiSearch aria-hidden="true" />
                  <input
                    type="search"
                    className="so-search"
                    aria-label={t("Search stock operations")}
                    placeholder={t(activeViewMeta.searchPlaceholder)}
                    value={requisitionSearch}
                    onChange={(event) => setRequisitionSearch(event.target.value)}
                  />
                  {requisitionSearch ? <button className="so-clearSearch" type="button" aria-label={t("Clear search")} onClick={() => setRequisitionSearch("")}><FiX aria-hidden="true" /></button> : null}
                </div>
              </div>
            </div>
            <div className="so-toolbar so-toolbar--filters">
              {activeView === "requests" ? (
                <div className="so-statusFilters" role="group" aria-label={t("Filter by status")}>
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      aria-pressed={statusFilter === option.value}
                      className={`so-statusChip ${statusFilter === option.value ? "so-statusChip--active" : ""}`}
                      onClick={() => setStatusFilter(option.value)}
                    >
                      {t(option.label)} <span>{option.count}</span>
                    </button>
                  ))}
                </div>
              ) : null}
              {renderStats(activeViewMeta.stats)}
            </div>
            {renderActiveView()}
          </>
        )}
      </section>

      {isAddOpen && (
        <AddStockRequisitionPopup
          isOpen={isAddOpen}
          businessUnitId={businessUnitId}
          onClose={() => {
            setIsAddOpen(false);
            fetchRequisitions();
          }}
        />
      )}

      {editingId && (
        <EditStockRequisitionPopup
          requisitionId={editingId}
          businessUnitId={businessUnitId}
          onClose={() => {
            setEditingId(null);
            fetchRequisitions();
            fetchDispatchNotes();
          }}
        />
      )}

      {dispatchingId && (
        <StockMovementPopup
          businessUnitId={businessUnitId}
          dispatchId={dispatchingNoteId}
          requisitionId={dispatchingId}
          onClose={() => {
            setDispatchingId(null);
            fetchRequisitions();
            fetchDispatchNotes();
          }}
        />
      )}

      {selectedDispatchId && (
        <ViewStockDispatchPopup
          dispatchId={selectedDispatchId}
          businessUnitId={businessUnitId}
          onClose={() => setSelectedDispatchId(null)}
        />
      )}
    </>
  );
}
